import { Fragment, useRef, useCallback } from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import useBlockSwipe, { DRAG_MAX, COMMIT_RATIO } from '../hooks/useBlockSwipe';
import usePullToRefresh from '../hooks/usePullToRefresh';
import { isMarkdownBlock } from '../services/markdown-detect';
import { splitTimeMark } from '../services/save-utils';

/**
 * V2 Block List — block cards with overlay action icons on hover (desktop)
 * and bidirectional swipe gestures (mobile).
 *
 * Swipe left  → enter move mode (select target node in sidebar)
 * Swipe right → open options panel (delete + future options)
 *
 * Props:
 *  - blocks           : string[] — block content strings (newest first)
 *  - onEditBlock      : (index, content) => void — opens edit modal
 *  - onAddBlock       : () => void — opens create modal
 *  - onMoveBlock      : (index) => void — fallback when no onSwipeCommit
 *  - onOpenOptions    : (filePath, index, content) => void
 *  - moveMode         : boolean
 *  - toolbar          : ReactNode (optional)
 *  - onSwipeProgress  : (direction, ratio) => void
 *  - onSwipeCommit    : (direction, filePath, index, content) => void
 *  - onSwipeCancel    : () => void
 *  - enableReorder    : boolean — allow drag-to-reorder (default true)
 *  - selectedFilePath : string — path of currently viewed file
 *  - onReorderEnd     : (fromIndex, toIndex) => void
 *  - onPullToRefresh  : () => void — pull-down past the top
 *  - refreshing       : boolean — a refresh is in flight (shows a hint + blocks re-trigger)
 */
export default function NodeV2BlockList({
  blocks = [],
  onEditBlock,
  onAddBlock,
  onMoveBlock,
  onOpenOptions,
  moveMode,
  swipeEnabled = true,
  toolbar,
  onSwipeProgress,
  onSwipeCommit,
  onSwipeCancel,
  onReorderEnd,
  selectedFilePath,
  enableReorder = true,
  onPullToRefresh,
  refreshing = false,
  unsyncedIndices,
  conflictIndices,
  onOpenConflict,
}) {
  const isEmpty = blocks.length === 0;
  const contentRefs = useRef({}); // index → content DOM element
  const cardRefs = useRef({});    // index → card DOM element (for reorder translateY)
  const scrollRef = useRef(null); // scroll container (for auto-scroll during reorder)

  // ── Shared bidirectional swipe hook ──────────────────────────
  const handleSwipeCommitWrapped = useCallback((direction, index) => {
    if (onSwipeCommit) {
      onSwipeCommit(direction, selectedFilePath, index, blocks[index]);
    } else {
      // Fallback: treat any swipe commit as "move"
      onMoveBlock(index);
    }
  }, [onSwipeCommit, onMoveBlock, selectedFilePath, blocks]);

  const handleReorderEndWrapped = useCallback((fromIndex, toIndex) => {
    if (onReorderEnd) {
      onReorderEnd(fromIndex, toIndex);
    }
  }, [onReorderEnd]);

  const { touchHandlers, mouseHandlers, swipedIndex, swipeProgress, swipeJustCommitted, closeSwipe, reorderState } = useBlockSwipe({
    enabled: swipeEnabled,
    moveMode,
    contentRefs,
    cardRefs,
    enableReorder: enableReorder && !moveMode,
    blockCount: blocks.length,
    scrollContainerRef: scrollRef,
    onSwipeProgress,
    onSwipeCommit: handleSwipeCommitWrapped,
    onSwipeCancel,
    onReorderEnd: handleReorderEndWrapped,
  });

  // ── Pull-to-refresh (mobile) ─────────────────────────────────
  // Disabled while reorder (swipeEnabled), move mode, or a refresh is
  // already in flight so the vertical-drag gestures don't fight each
  // other and duplicate refreshes are blocked at the gesture level.
  const { pull, phase } = usePullToRefresh({
    scrollRef,
    onRefresh: onPullToRefresh,
    enabled: !moveMode && !swipeEnabled && !refreshing,
  });

  // ── Click on card body ───────────────────────────────────────
  const handleCardClick = (e, index) => {
    if (moveMode) return;
    // Suppress click after a swipe/drag — the swipe already handled the action
    if (swipeJustCommitted.current) {
      swipeJustCommitted.current = false;
      return;
    }
    if (swipedIndex === index) {
      closeSwipe();
      return;
    }
    // Open edit modal for this block
    onEditBlock(index, blocks[index]);
  };

  return (
    <div className={`v2-block-list scroll-y${enableReorder && swipeEnabled && !moveMode ? ' reorder-enabled' : ''}`} ref={scrollRef}>
      {/* Pull-to-refresh indicator — reveals as the user pulls down at the top */}
      <div
        className={`v2-pull-indicator${phase === 'ready' ? ' is-ready' : ''}`}
        style={{ height: pull }}
      >
        <span className="v2-pull-indicator-arrow" aria-hidden="true">↓</span>
        <span>{phase === 'ready' ? 'Release to refresh' : 'Pull to refresh'}</span>
      </div>

      {/* Refreshing indicator — visible while content is being re-fetched */}
      {refreshing && (
        <div className="v2-refresh-indicator">
          <span className="spinner" />
          <span>Refreshing...</span>
        </div>
      )}

      {/* Toolbar: add block + actions */}
      <div className="v2-block-toolbar">
        <div
          className="v2-block-card-new"
          onClick={() => { if (!moveMode) onAddBlock(); }}
          tabIndex={0}
          role="button"
          onKeyDown={(e) => {
            if (!moveMode && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              onAddBlock();
            }
          }}
        >
          <span className="v2-block-card-plus">+</span>
          <span className="v2-block-card-new-text">New block...</span>
        </div>
        {toolbar}
      </div>

      {isEmpty ? (
        <div className="v2-block-empty">
          No blocks yet. Click the card above to add one.
        </div>
      ) : (
        blocks.map((content, index) => {
          // ── Edge-strip state for this card ──────────────────
          const strip = (swipeProgress && swipeProgress.index === index)
            ? swipeProgress
            : null;
          const stripWidth = strip ? Math.round(strip.ratio * DRAG_MAX) : 0;
          const stripCommitted = strip && strip.ratio >= COMMIT_RATIO;
          const stripBg = stripCommitted
            ? 'rgba(var(--accent-rgb), 0.25)'
            : 'rgba(128, 128, 128, 0.15)';

          // A leading time record is shown as a badge above the body.
          const { badge, body } = splitTimeMark(content);

          return (
          <Fragment key={index}>
            {/* Drop indicator — show before the card at dragIndex (but not before the dragged card) */}
            {reorderState && reorderState.dragIndex === index && reorderState.index !== index && (
              <div className="v2-reorder-indicator" />
            )}
          <div
            className="v2-block-card"
            ref={(el) => { cardRefs.current[index] = el; }}
            onClick={(e) => handleCardClick(e, index)}
            onTouchStart={(e) => touchHandlers.onTouchStart(e, index)}
            onTouchMove={(e) => touchHandlers.onTouchMove(e, index)}
            onTouchEnd={(e) => touchHandlers.onTouchEnd(e, index)}
            onMouseDown={(e) => mouseHandlers.onMouseDown(e, index)}
            tabIndex={0}
            role="button"
            onKeyDown={(e) => {
              if (moveMode) return;
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onEditBlock(index, blocks[index]);
              }
            }}
          >
            {/* ── Swipe progress edge strips ──────────────── */}
            {strip && strip.direction === 'left' && (
              <div
                className="v2-swipe-strip"
                data-side="left"
                style={{
                  left: 0,
                  width: stripWidth,
                  background: stripBg,
                }}
              />
            )}
            {strip && strip.direction === 'right' && (
              <div
                className="v2-swipe-strip"
                data-side="right"
                style={{
                  right: 0,
                  width: stripWidth,
                  background: stripBg,
                }}
              />
            )}

            {/* Content — render Markdown when detected, else plain text.
                A leading time record is shown as a badge above the body. */}
            <div
              className="v2-block-card-content"
              ref={(el) => { contentRefs.current[index] = el; }}
            >
              {badge && <div className="v2-block-time-badge">{badge}</div>}
              {body ? (
                isMarkdownBlock(body) ? (
                  <div
                    className="v2-block-md help-doc-content"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(marked.parse(body)) }}
                  />
                ) : (
                  body
                )
              ) : null}
            </div>

            {/* Conflict / unsynced badge — conflicted blocks take precedence */}
            {conflictIndices?.has(index) ? (
              <span
                className="v2-block-conflict"
                title="Conflict — tap to resolve"
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); if (onOpenConflict) onOpenConflict(); }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    if (onOpenConflict) onOpenConflict();
                  }
                }}
              >!</span>
            ) : unsyncedIndices?.has(index) && (
              <span className="v2-block-unsynced" title="Unsynced changes" />
            )}

          </div>
          </Fragment>
          );
        })
      )}

      {/* Drop indicator at end of list (dragging past last card) */}
      {reorderState && reorderState.dragIndex >= blocks.length && (
        <div className="v2-reorder-indicator" />
      )}
    </div>
  );
}
